import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
  const API_BASE = BACKEND_URL+"/api";
  
  // Debug logging
  console.log('AuthContext - Backend URL:', BACKEND_URL);
  console.log('AuthContext - API_BASE:', API_BASE);

  // Configure axios defaults
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  }, [token]);

  // Check authentication status on mount
  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    if (!token) {
      console.log('No token found, user not authenticated');
      setLoading(false);
      return;
    }

    try {
      console.log('Checking auth with token:', token.substring(0, 20) + '...');
      const response = await axios.get(`${API_BASE}/auth/me`);
      console.log('Auth check successful:', response.data);
      setUser(response.data);
    } catch (error) {
      console.error('Auth check failed:', error.response?.status, error.response?.data);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (emailOrUserData, password, turnstileToken = null) => {
    // Handle direct user data update (used by registration flow)
    if (typeof emailOrUserData === 'object' && !password) {
      console.log('Setting user data directly:', emailOrUserData);
      setUser(emailOrUserData);
      return { success: true, user: emailOrUserData };
    }

    // Handle normal login flow
    try {
      console.log('Attempting login for:', emailOrUserData);
      const response = await axios.post(`${API_BASE}/auth/login`, {
        email: emailOrUserData,
        password,
        turnstile_token: turnstileToken
      });

      console.log('Login response:', response.data);
      const { access_token, user: userData } = response.data;
      
      localStorage.setItem('token', access_token);
      setToken(access_token);
      setUser(userData);
      
      console.log('Login successful, token stored');
      return { success: true, user: userData };
    } catch (error) {
      console.error('Login failed:', error.response?.status, error.response?.data);
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Login failed' 
      };
    }
  };

  const register = async (email, password, fullName, turnstileToken = null) => {
    try {
      const response = await axios.post(`${API_BASE}/auth/register`, {
        email,
        password,
        full_name: fullName,
        turnstile_token: turnstileToken
      });

      // Auto-login after successful registration (skip turnstile for auto-login)
      const loginResult = await login(email, password);
      return loginResult;
    } catch (error) {
      console.error('Registration failed:', error);
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Registration failed' 
      };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    delete axios.defaults.headers.common['Authorization'];
  };

  const refreshUser = async () => {
    if (!token) return;
    
    try {
      console.log('Refreshing user data...');
      const response = await axios.get(`${API_BASE}/auth/me`);
      console.log('User data refreshed:', response.data);
      setUser(response.data);
      return response.data;
    } catch (error) {
      console.error('Failed to refresh user data:', error);
      return null;
    }
  };

  const value = {
    user,
    loading,
    login,
    register,
    logout,
    refreshUser,
    isAuthenticated: !!user
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};